import styled from '@emotion/native';
import type { AllergyCode, AllergyCreateRequest } from '@quromedical/models';
import { Row } from 'components/base';
import type { SubmissionResult } from 'components/form';
import { Button, type TextVariant } from 'design-system';
import { MARGIN } from 'design-system/theme';
import { useList } from 'hooks/useList';
import { useEffect, useRef, useState } from 'react';
import { AllergyCreateForm } from './AllergyCreateForm';

interface FormProps {
  handleSubmit?: (
    data: AllergyCreateRequest,
  ) => Promise<SubmissionResult<unknown>>;
  patient?: string;
  title?: string;
  titleVariant?: TextVariant;
  showSubmitButton?: boolean;
  onValidChange?: (data: AllergyCode[]) => void;
}

type AllergyFormState = {
  data: Partial<AllergyCode>;
  hasErrors: boolean;
  hasSubmitted: boolean;
};

const ButtonWrapper = styled(Row)({
  marginTop: MARGIN['2xs'],
  width: '100%',
  justifyContent: 'flex-end',
});

export const AllergyListForm = ({
  handleSubmit,
  title,
  patient,
  titleVariant,
  showSubmitButton,
  onValidChange,
}: FormProps) => {
  const list = useList<number, AllergyFormState>([
    {
      key: Math.random(),
      value: {
        data: {},
        hasErrors: true,
        hasSubmitted: false,
      },
    },
  ]);

  const previousAllergyItemsRef = useRef<AllergyCode[]>([]);
  useEffect(() => {
    const updatedAllergyItems = list.items
      .filter((item) => item.value.data)
      .map((item) => item.value.data as AllergyCode);

    if (
      JSON.stringify(previousAllergyItemsRef.current) !==
      JSON.stringify(updatedAllergyItems)
    ) {
      previousAllergyItemsRef.current = updatedAllergyItems;
      onValidChange?.(updatedAllergyItems);
    }
  }, [list.items, onValidChange]);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const isSubmitButtonVisible = showSubmitButton && patient && handleSubmit;

  return (
    <>
      {list.items.map((p, index) => {
        const isSubmittedItem = p.value.hasSubmitted || hasSubmitted;

        return (
          <AllergyCreateForm
            key={p.key}
            title={title}
            titleVariant={titleVariant}
            onAdd={() => {
              list.update(p.key, {
                ...p.value,
                hasErrors: false,
                hasSubmitted: true,
              });
              list.add(Math.random(), {
                data: {},
                hasErrors: true,
                hasSubmitted: false,
              });
            }}
            onChange={(data, hasErrors) => {
              list.update(p.key, {
                hasSubmitted: p.value.hasSubmitted,
                data,
                hasErrors,
              });
              list.items.every((item) => !item.value.hasErrors);

              const allergyItems = list.items.map(
                (p) => p.value.data as AllergyCode,
              );

              onValidChange?.(allergyItems);
            }}
            onRemove={() => {
              list.remove(p.key);
            }}
            canAdd={index === list.items.length - 1}
            canRemove={list.items.length > 1}
            showErrors={isSubmittedItem && p.value.hasErrors}
            initialValues={p.value.data}
          />
        );
      })}
      {isSubmitButtonVisible ? (
        <ButtonWrapper>
          <Button
            variant="contained"
            onPress={() => {
              setHasSubmitted(true);
              const allValid = list.items.every(
                (item) => !item.value.hasErrors,
              );
              if (!allValid) {
                return;
              }

              const allergyItems = list.items.map(
                (p) => p.value.data as AllergyCode,
              );

              const result: AllergyCreateRequest = {
                items: allergyItems,
                patient,
              };
              handleSubmit(result);
            }}
            text={'Submit'}
          />
        </ButtonWrapper>
      ) : undefined}
    </>
  );
};
